/**
 * Physical dimensions shared by the domain layer and the 3D engines.
 *
 * These are facts about the building, not rendering details, so the DOMAIN owns
 * them and `src/engines/` imports upward from here — never the other way round.
 * `src/engines/constants.ts` re-exports them under their `ENGINE_*` names so
 * existing engine call sites keep working.
 *
 * WHY THIS MODULE EXISTS: `domain/chimney/calculateHeaterPlacement.ts` used to
 * carry local mm literals mirroring the engine constants, with a comment saying
 * the copies existed "so the domain layer stays free of engine imports". Five
 * constants were mirrored and one had already drifted — the panel thickness was
 * 15 mm in the domain copy against 20 mm in the rule registry, so heater
 * placement measured every room 10 mm too wide and silently ignored any admin
 * `rule_overrides` for that dimension. Duplication was never the price of a
 * clean layer boundary; a shared bottom-layer module is.
 *
 * See docs/notes/2026-07-28-mirrored-constants-drift-silently.md
 */

import { getRuleValue, type RuleOverrides } from "../../data/ruleRegistry";
import {
	type HeaterSelection,
	resolveHeaterModel,
} from "../heater/heaterCatalog";

// ─── Framing (from the rule registry, so admin overrides apply everywhere) ───

/** Stud depth — the 95 in a 45×95 regel. */
export const STUD_D_M = getRuleValue<number>("dimensions.studDepthM");
/** Stud width — the 45 in a 45×95 regel. */
export const STUD_W_M = getRuleValue<number>("dimensions.studWidthM");
/** Bastupanel thickness. */
export const PANEL_T_M = getRuleValue<number>("dimensions.panelThicknessM");
/** Floor/ceiling joist cross-section. */
export const JOIST_W_M = getRuleValue<number>("dimensions.joistWidthM");
export const JOIST_H_M = getRuleValue<number>("dimensions.joistHeightM");
/** Stud/joist spacing, centre to centre. */
export const CC_M = getRuleValue<number>("framing.ccMm") / 1000;
/**
 * DEFAULT wall height — what a design gets when the user has not chosen one
 * (every design saved before issue 155). Anything that builds or measures a
 * specific design must go through `resolveWallHeightM(input.wallHeightM)`,
 * never this constant: the height is a user choice.
 */
export const WALL_H_M = getRuleValue<number>("dimensions.wallHeightM");

/** Selectable wall height range (issue 155), metres. */
export const WALL_H_MIN_M = 2.0;
export const WALL_H_MAX_M = 2.6;
export const WALL_H_STEP_M = 0.05;

/**
 * The wall height a design builds with: the user's choice, snapped to the
 * 5 cm step and clamped to the selectable range, or the rule default when
 * the design has none. Snapping keeps a stray 2.1000000001 from a slider or
 * a share link from rebuilding the scene or shifting a cut length.
 */
export function resolveWallHeightM(
	value?: number | null,
	overrides?: RuleOverrides,
): number {
	const fallback = overrides
		? getRuleValue<number>("dimensions.wallHeightM", overrides)
		: WALL_H_M;
	if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
	const snapped = Math.round(value / WALL_H_STEP_M) * WALL_H_STEP_M;
	const clamped = Math.min(WALL_H_MAX_M, Math.max(WALL_H_MIN_M, snapped));
	return Math.round(clamped * 100) / 100;
}

/**
 * A stored/shared wall height: `null` when the design has none (the default
 * builds), otherwise the snapped, clamped value. Anything that is not a finite
 * number — a pre-155 row, a hand-edited share link — reads as "none".
 */
export function normalizeWallHeightM(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value)
		? resolveWallHeightM(value)
		: null;
}

// ─── Interior wall lining stack ───
// regel → isolering → bastufolie (ångspärr) → LIGGANDE glespanel → STÅENDE bastupanel

/** Rendered thickness of the aluminium bastufolie plane (≤2 mm per spec). */
export const VAPOR_BARRIER_T_M = 0.0015;
/** Glespanel / inv. spikläkt cross-section — 22×45 mm, same as the BOM row. */
export const INTERIOR_BATTEN_D_M = 0.022;
export const INTERIOR_BATTEN_W_M = 0.045;
/**
 * Total interior build-out inward from the stud's inner face before the
 * bastupanel starts. Every room dimension (inner width/length, bench widths,
 * heater clearances, ceiling extents, drag bounds) measures through this.
 */
export const INTERIOR_LINING_D_M = VAPOR_BARRIER_T_M + INTERIOR_BATTEN_D_M;

// ─── Heater bounding box ───
// Overall envelope per heater type, used for placement, clearance and the 3D
// model alike. Wood: Harvia M3 / Narvi Luosto class. Electric: tower + fins.

const WOOD_HEATER = { w: 0.39, h: 0.71, d: 0.45 } as const;
const ELECTRIC_HEATER = { w: 0.3, h: 0.9, d: 0.3 } as const;

type HeaterKind = "wood" | "electric";

/**
 * A heater type alone (today's default box) or a design's selection, whose
 * catalog model — when one of the matching type is chosen — supplies the box
 * instead (issue 159).
 */
export type HeaterRef = HeaterKind | HeaterSelection;

const heaterBox = (ref: HeaterRef): { w: number; h: number; d: number } => {
	if (typeof ref !== "string") {
		const model = resolveHeaterModel(ref);
		if (model) {
			return {
				w: model.bodyMm.width / 1000,
				h: model.bodyMm.height / 1000,
				d: model.bodyMm.depth / 1000,
			};
		}
		return ref.heaterType === "wood" ? WOOD_HEATER : ELECTRIC_HEATER;
	}
	return ref === "wood" ? WOOD_HEATER : ELECTRIC_HEATER;
};

/** Overall width of the heater. */
export function getHeaterWidthM(ref: HeaterRef): number {
	return heaterBox(ref).w;
}

/** Overall depth of the heater (in-room body; excludes a feed tunnel). */
export function getHeaterDepthM(ref: HeaterRef): number {
	return heaterBox(ref).d;
}

/** Overall height of the heater. */
export function getHeaterHeightM(ref: HeaterRef): number {
	return heaterBox(ref).h;
}

/** The heater type of a ref, whether it came as a bare type or a selection. */
export function heaterKindOf(ref: HeaterRef): HeaterKind {
	return typeof ref === "string" ? ref : ref.heaterType;
}

/** The chosen catalog model id of a ref, if any. */
export function heaterModelIdOf(ref: HeaterRef): string | null | undefined {
	return typeof ref === "string" ? undefined : ref.heaterModelId;
}

// ─── mm helpers ───
// The domain's placement math works in millimetres; converting here keeps the
// conversion in one place instead of at every call site.

export const toMm = (metres: number): number => metres * 1000;
